import React, { useEffect, useState } from 'react';
import { fetchWeeklyComparison } from '../services/weeklyService';
import { formatDateTime } from '../utils/dateHelper';
import { useAuth } from '../context/useAuth'; // Import useAuth to access user context

const WeeklyRevenueDashboard = ({stream}) => {
  const [data, setData] = useState([]);
  const [subcounties, setSubcounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth(); // Access user from context

  const now = new Date();
// Inside WeeklyTrendChart.jsx or WeeklyRevenueDashboard.jsx
useEffect(() => {
  const loadData = async () => {
    setLoading(true);
    try {
      // Pass the stream prop to the service call
      const result = await fetchWeeklyComparison(stream); 
        // Filter the sub-county headers based on user context  
            const filteredSubcounties = user?.sub_county_id 
                ? result.subcounties.filter(sc => sc.id === user.sub_county_id)
                : result.subcounties;
      setData(result.data);
      setSubcounties(filteredSubcounties);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  loadData();
}, [stream, user?.sub_county_id]); // <--- Ensure 'stream' and 'user.sub_county_id' are here!

  if (loading) return <div className="p-10 text-center">Analyzing Weekly Trends...</div>;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <h2 className="text-xs font-bold text-gray-800">Weekly Sub-County Revenue Comparison ({stream || 'All Streams'})</h2>
        <p className="text-sm text-gray-500">Rolling 5-week performance matrix</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-700 uppercase bg-gray-100">
            <tr>
              <th className="px-3 py-3 sticky left-0 bg-gray-100 z-10 border-r">Week Period (Mon - Sun)</th>
              <th className="px-3 py-3 text-blue-800 font-bold border-r">Total County</th>
               {subcounties.map(sc => (
                <th 
                  key={sc.id} 
                  className="px-6 py-4 border-l cursor-help underline decoration-dotted decoration-slate-300 hover:bg-gray-200 transition-colors"
                  title={`RO: ${sc.username || 'Not Assigned'}`}
                >
                  {sc.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((week, idx) => (
              <tr key={idx} className="hover:bg-blue-50 transition-colors">
                <td className="px-3 py-3 sticky left-0 bg-white font-medium border-r">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <div className={`${week.status === 'open' ? 'text-red-700 font-bold' : 'text-gray-900 font-bold' }`}>
                      {formatDateTime(week.start_date)} - {formatDateTime(week.end_date)}
                    </div>
                    {week.status === 'open' && (
                      <div className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-red-700 bg-red-100 border border-red-300 rounded-full">
                        {/* Blinking Dot */}
                        <div className="relative flex h-2 w-2">
                          <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"></div>
                          <div className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 font-bold text-blue-700 border-r bg-blue-50/30">
                  {Number(week.total_weekly_revenue).toLocaleString()}
                </td>
                {subcounties.map(sc => (
                  <td key={sc.id} className="px-3 py-3">
                    {Number(week[sc.name] || 0).toLocaleString()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyRevenueDashboard;